/**
 * Pairing-link parsing — shared by the manual-URL field, the `?bridge=`
 * query-param flow, and the in-app QR scanner, so all three accept exactly
 * the same inputs.
 *
 * OPBridge's dashboard "Pair a new device" panel encodes, and its Copy
 * button hands out, a full pairing link:
 *
 *     https://opbuttons.overpoweredapps.net/connect?bridge=<encoded bridge url>
 *
 * while a human typing an address enters the bridge URL directly. Both are
 * legitimate; anything that accepts one must accept the other, or a pasted /
 * scanned link fails a `^https?://` check for the wrong reason (it IS a URL,
 * just not the bridge's).
 */

/** Pull the bridge URL out of a full pairing link, or pass a bare bridge URL
 *  through untouched. Never throws — an unparseable string comes back as-is
 *  so the caller's own validation produces the error message. */
export function extractBridgeUrl(raw: string): string {
  const trimmed = raw.trim();
  try {
    const inner = new URL(trimmed).searchParams.get('bridge');
    if (inner) return inner.trim();
  } catch {
    /* not a parseable URL — fall through */
  }
  return trimmed;
}

/** A computer's 16-character code alone (or its suite hostname without the
 *  scheme/port) becomes its full suite address. 2026-10-18, operator minutes
 *  before a gig, the QR route stuck and the iPad keyboard over the field:
 *  "there should be a quick input so i just have to type the id in". Anything
 *  else passes through untouched for the caller's own validation. */
export function expandBridgeCode(raw: string): string {
  const m = /^([0-9a-f]{16})(\.bridge\.overpoweredapps\.net)?(:\d+)?\/?$/i.exec(raw.trim());
  if (!m) return raw;
  return `https://${m[1]!.toLowerCase()}.bridge.overpoweredapps.net${m[3] ?? ':8768'}`;
}

/** True when `raw` (after extraction) looks like something we can hand to
 *  the HTTP client. Deliberately permissive about host/port — reachability
 *  is the probe's job, not a regex's. */
export function looksLikeBridgeUrl(raw: string): boolean {
  return /^https?:\/\/.+/.test(extractBridgeUrl(raw));
}

/** The WebTransport offline path, carried in a pairing link. */
export interface ScannedWtAdvert {
  /** e.g. `https://192.168.50.51:4433` */
  url: string;
  /** Every pin in the link: the current one plus any pre-minted chain. */
  pins: string[];
  /** Unix seconds the last pin expires, when the link says. */
  untilUnixS: number | null;
}

/**
 * Pull the WebTransport advert out of a pairing link, when the dashboard put
 * one there.
 *
 * WHY THIS EXISTS (2026-09-01). The offline path is armed by caching the
 * bridge's cert pin from `/health` — which needs the suite hostname to
 * resolve, which needs internet. So a device that has been away longer than
 * the pin's life arrives at a venue with a dead pin and **no way to learn a
 * new one**, because the only channel that could teach it is the one that is
 * down. That is a silent failure discovered at the worst moment.
 *
 * A QR carries the pin over light instead of over the network, so it needs no
 * DNS, no internet and no phone signal — the dashboard is already open on the
 * laptop that runs the bridge. This is the break-glass path.
 *
 * Link shape (all params optional; absent means "no advert here"):
 *
 *     ...?bridge=<url>&wt=<https://ip:port>&pin=<64hex>[&pins=<hex,hex,...>][&until=<unix>]
 *
 * Returns null unless BOTH a `wt` origin and at least one valid pin are
 * present — a half-formed advert must never be stored, or a device ends up
 * pinned to something it cannot dial.
 */
export function extractWtAdvert(raw: string): ScannedWtAdvert | null {
  let params: URLSearchParams;
  try {
    params = new URL(raw.trim()).searchParams;
  } catch {
    return null; // a bare bridge URL carries no advert, which is not an error
  }
  const url = params.get('wt')?.trim();
  if (!url || !/^https:\/\/.+:\d+$/.test(url)) return null;
  // The door must be a LAN address (OPMod's ask, 2026-10-05). A WebTransport
  // door is the bridge's own listener, learned from `/health.ip`, so a public
  // address or a hostname is never legitimate - and every WebTransport request
  // carries the bearer, so a link pointing one elsewhere would hand it over.
  let wtHost = '';
  try { wtHost = new URL(url).hostname; } catch { return null; }
  if (!isLanHostLiteral(wtHost)) return null;

  const pins: string[] = [];
  const add = (v: string | null | undefined) => {
    for (const part of (v ?? '').split(',')) {
      const hex = part.trim().toLowerCase();
      // Validated here rather than trusted: a malformed pin would otherwise
      // reach `hexToBytes` and throw inside the dial, turning a bad QR into
      // what looks like a transport failure.
      if (/^[0-9a-f]{64}$/.test(hex) && !pins.includes(hex)) pins.push(hex);
    }
  };
  add(params.get('pin'));
  add(params.get('pins'));
  if (pins.length === 0) return null;

  const untilRaw = Number(params.get('until'));
  const untilUnixS = Number.isFinite(untilRaw) && untilRaw > 0 ? Math.floor(untilRaw) : null;
  return { url, pins, untilUnixS };
}

/**
 * True for an IP literal no one outside the LAN can own: RFC 1918, loopback,
 * link-local, CGNAT / Tailscale (100.64/10) and IPv6 loopback, ULA and
 * link-local. Hostnames are refused: `/health.ip` is always a literal, and a
 * name can resolve anywhere. `host` is a URL `hostname` (IPv6 in brackets).
 */
export function isLanHostLiteral(host: string): boolean {
  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (v4) {
    const [a, b] = [Number(v4[1]), Number(v4[2])];
    if (v4.slice(1).some((o) => Number(o) > 255)) return false;
    return a === 10
      || a === 127
      || (a === 172 && b >= 16 && b <= 31)
      || (a === 192 && b === 168)
      || (a === 169 && b === 254)
      || (a === 100 && b >= 64 && b <= 127);
  }
  const v6 = /^\[([0-9a-f:.]+)\]$/i.exec(host)?.[1]?.toLowerCase();
  if (!v6) return false;
  if (v6 === '::1') return true;
  const first = parseInt(v6.split(':')[0] || '0', 16);
  return (first & 0xfe00) === 0xfc00   // fc00::/7, unique local
      || (first & 0xffc0) === 0xfe80;   // fe80::/10, link-local
}
